"use client";

/**
 * `<ReportsHeroStats>` — the `/reports` hero stat row
 * (9 phases / 3 free per month / 2 markets) with count-ups.
 * All three stats are about the 9-phase Reports product — Compass's
 * 7-analyzer loop is a different product and does not appear here.
 *
 * Client island inside the server-rendered hero (same pattern as the
 * homepage `<HeroStats>`, with Reports-specific labels): numeric stats
 * ease 0 → target via `useCountUp` (600ms, the motion-system count-up
 * duration); "Weekly" renders statically. Under prefers-reduced-motion
 * the hook renders the final numbers instantly. Labels are plain text
 * and therefore present in the SSR HTML.
 */

import { useCountUp } from "@/lib/motion";

interface StatDef {
  /** Numeric stats count up; string stats render statically. */
  value: number | string;
  label: string;
}

const STATS: StatDef[] = [
  { value: 9, label: "Analysis phases per report" },
  { value: 3, label: "Free reports a month, on the house" },
  { value: "US + IN", label: "Markets covered: NYSE/NASDAQ & NSE/BSE" },
];

function StatValue({ value }: { value: number | string }) {
  // Hooks can't be conditional — run the count-up unconditionally and
  // only use it when the stat is numeric.
  const counted = useCountUp(typeof value === "number" ? value : 0);
  return (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>
      {typeof value === "number" ? Math.round(counted) : value}
    </span>
  );
}

export function ReportsHeroStats() {
  return (
    <div
      style={{
        marginTop: 56,
        paddingTop: 28,
        borderTop: "1px solid var(--color-line-2)",
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 24,
        maxWidth: 600,
      }}
    >
      {STATS.map((s) => (
        <div key={s.label}>
          <div
            style={{
              fontSize: 26,
              fontWeight: 600,
              color: "var(--color-text)",
              letterSpacing: "-0.02em",
            }}
          >
            <StatValue value={s.value} />
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--color-dim)",
              marginTop: 4,
              letterSpacing: "0.02em",
              lineHeight: 1.4,
            }}
          >
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}
